import axios, { AxiosPromise } from "axios";
import { env } from "../env/env";
import { ops } from "./config";

// Mirrors the backend YextProfileDto. All fields optional — provided values overlay
// the stored Location data when syncing. Sections post only their own slice.
export interface YextProfileTimeRange {
  start: number; // minutes from midnight
  end: number;
}

export interface YextProfileDayHours {
  open: boolean;
  ranges: YextProfileTimeRange[];
}

export type YextDayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type YextProfileHours = Partial<Record<YextDayKey, YextProfileDayHours>>;

export interface YextProfileLinks {
  onlineOrdering?: string[];
  reservations?: string[];
  menu?: string[];
  social?: Record<string, string>;
  giftCard?: string[];
}

export interface YextProfileAmenities {
  cuisineTypes?: string;
  serviceOptions?: string[];
  reservationSupport?: string;
  orderingLinks?: string[];
  priceRange?: string;
  alcoholServed?: string[];
  outdoorSeating?: string;
  wifiAvailable?: string;
  parking?: string[];
  accessibility?: string[];
  familyFriendly?: string[];
}

// One staff bio (Name + Bio text); pushed to Yext as a Bios ECL entry.
export interface YextStaffBio {
  name: string;
  bio: string;
}

// Full hosted image URLs (S3), read-only and server-derived from the location's brand kit
// (managed in Brand Kit & Assets): logo is the kit's logo, photoGallery its photos.
export interface YextProfileVisualAssets {
  logo?: string;
  photoGallery?: string[];
}

export interface YextProfile {
  businessName?: string;
  alternateNames?: string;
  category?: string[];
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  suppressAddress?: boolean;
  displayAddress?: string;
  latitude?: string;
  longitude?: string;
  primaryPhone?: string;
  secondaryPhone?: string;
  email?: string;
  website?: string;
  websiteDisplayUrl?: string;
  descriptionShort?: string;
  descriptionLong?: string;
  featuredMessage?: string;
  categories?: string[];
  openingDate?: string;
  staffBios?: YextStaffBio[];
  hours?: YextProfileHours;
  links?: YextProfileLinks;
  amenities?: YextProfileAmenities;
  visualAssets?: YextProfileVisualAssets;
  // Read-only: false when these values were seeded from the base Location (first-time setup),
  // true when loaded from a previously saved profile.
  existing?: boolean;
}

export interface YextSyncResult {
  entityId: string;
}

// Onboarding gate: whether this location already has a Yext entity.
export interface YextStatus {
  hasEntity: boolean;
}

// Normalized scan status driving the Status-tab chip.
export type YextListingStatus =
  | "LIVE"
  | "NOT_VERIFIED"
  | "WRONG_PHONE"
  | "INCORRECT_INFO"
  | "NOT_FOUND"
  | "PROCESSING"
  | "OPTED_OUT"
  | "NEEDS_CONNECTION"; // owned publisher (Google/Facebook) awaiting the customer's account link

export interface YextListing {
  publisherId: string;
  publisherName: string;
  status: YextListingStatus;
  rawStatus?: string; // raw Yext status (LIVE, WAITING_ON_*, OPTED_OUT, UNAVAILABLE)
  listingUrl?: string;
  message?: string;
}

// The Status tab payload: managed name/address/phone (shown in every row) + per-publisher rows.
export interface YextListingsView {
  businessName?: string;
  address?: string;
  phone?: string;
  listings: YextListing[];
}

export interface YextPublishResult {
  publisherCount: number;
}

// ---- Reports (analytics) ----
export interface YextReportTotals {
  impressions: number;
  interactions: number;
  profileViews: number;
  searches: number;
}

export interface YextReportPoint {
  date: string; // YYYY-MM-DD
  impressions: number;
  interactions: number;
}

export interface YextPublisherStat {
  publisher: string;
  primaryLabel: string;
  primaryValue: number;
  secondaryLabel?: string;
  secondaryValue?: number;
}

export interface YextReportHealth {
  listingsLive: number;
  accuracy: number; // percent 0–100
  duplicatesSuppressed: number;
}

export interface YextReports {
  connected: boolean;
  rangeDays: number;
  totals: YextReportTotals;
  series: YextReportPoint[];
  publishers: YextPublisherStat[];
  health: YextReportHealth;
}

export interface YextMenuTile {
  id: number;
  designId: string;
  name: string;
  status: "not_live" | "live" | "paused";
}

export interface YextMenuTileRequest {
  designId: string;
  name: string;
  status?: string;
}

/** Pulls the backend/Yext error message out of an axios error, falling back to a generic one. */
export function yextErrorMessage(e: any, fallback: string): string {
  return e?.response?.data?.message || fallback;
}

/** Prepends https:// to a bare host so URL fields are valid (mirrors the backend). Blank stays blank. */
export function ensureHttps(url: string): string {
  const v = url.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

const YextApi = {
  /** The locally-stored profile for a location (fields absent if nothing saved yet). */
  getProfile(locationId: number): AxiosPromise<YextProfile> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/profile`, ops());
  },

  /** Onboarding gate: whether this location already has a Yext entity. */
  getStatus(locationId: number): AxiosPromise<YextStatus> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/status`, ops());
  },

  /** Persist the location's profile locally and push it to Yext (identity, hours, links, amenities). */
  syncLocation(locationId: number, profile: YextProfile): AxiosPromise<YextSyncResult> {
    return axios.post(`${env.apiPath}/yext/locations/${locationId}/sync`, profile, ops());
  },

  /** Build a menu from a design's content and push it to the location's Yext listing. */
  pushMenuFromDesign(locationId: number, designId: string): AxiosPromise<YextSyncResult> {
    return axios.post(`${env.apiPath}/yext/locations/${locationId}/menu/design/${designId}`, {}, ops());
  },

  /** Scan view: managed NAP + per-publisher statuses (listings empty until synced). */
  getListings(locationId: number): AxiosPromise<YextListingsView> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/listings`, ops());
  },

  /** Publish the location to all available publishers. */
  publish(locationId: number): AxiosPromise<YextPublishResult> {
    return axios.post(`${env.apiPath}/yext/locations/${locationId}/publish`, {}, ops());
  },

  /** Listings analytics for the Reports tab over the trailing `days` window. */
  getReports(locationId: number, days = 30): AxiosPromise<YextReports> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/reports`, { ...ops(), params: { days } });
  },

  // ---- Persisted Home-tab menu tiles ----
  getMenus(locationId: number): AxiosPromise<YextMenuTile[]> {
    return axios.get(`${env.apiPath}/yext/locations/${locationId}/menus`, ops());
  },
  createMenu(locationId: number, req: YextMenuTileRequest): AxiosPromise<YextMenuTile> {
    return axios.post(`${env.apiPath}/yext/locations/${locationId}/menus`, req, ops());
  },
  updateMenu(locationId: number, id: number, req: YextMenuTileRequest): AxiosPromise<YextMenuTile> {
    return axios.put(`${env.apiPath}/yext/locations/${locationId}/menus/${id}`, req, ops());
  },
  deleteMenu(locationId: number, id: number): AxiosPromise<void> {
    return axios.delete(`${env.apiPath}/yext/locations/${locationId}/menus/${id}`, ops());
  },
};

export default YextApi;
